/* eslint-disable @reatom/unit-naming-rule */
import { action, atom, reaction, spawn, withAssign } from '@reatom/framework'

import { Result } from '@blade/std-lib'
import { nativeNetworkCurrencyAtom } from '@root/entities/currency/atoms'
import { CurrencyModel } from '@root/entities/currency/model'
import { Network } from '@root/entities/network/model'
import { solToLamports } from '@root/entities/network/model/solana'
import { PathfinderUtil, Quote } from '@root/entities/quote/model'
import { QuoteNotFoundError } from '@root/entities/quote/model/quote-error'
import { joinedInviteCodeAtom } from '@root/entities/referral/atoms'
import { TradeSettingsSolRpc } from '@root/entities/trade-settings/model'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import {
  solSelectedRpcAtom,
  tradeSettingsAtom,
} from '@root/features/trade/trade-settings/atoms'
import { tradingWalletAtom } from '@root/features/trade/trading/atoms'
import { UnknownError } from '@root/shared'
import { reatomAsyncState } from '@root/shared/lib'

import type { AsyncStateN } from '@blade/std-lib'
import type { ReactionAtom, Unsubscribe } from '@reatom/framework'
import type { PairModel } from '@root/entities/pair/atoms'
import type { SwapDirection } from '@root/entities/trade-type/model/trade-type'
import type { BigNumber } from 'bignumber.js'

export type QuoteState = AsyncStateN.AsyncStateN<
  UnknownError | QuoteNotFoundError,
  { quote: Quote.Quote; timestamp: Date }
>

export const reatomPathfinderQuoteSubscription = ({
  pair,
  amount,
  type,
}: {
  pair: PairModel
  amount: BigNumber
  type: SwapDirection
}) => {
  const quoteStateAtom = reatomAsyncState.notInitiated<
    AsyncStateN.InferError<QuoteState>,
    AsyncStateN.InferSuccess<QuoteState>
  >()
  const subscribedAtAtom = atom<Date | undefined>(undefined)
  const firstQuoteReceivedAtAtom = atom<Date | undefined>(undefined)
  const quoteAtom = atom((ctx) => {
    const quoteState = ctx.spy(quoteStateAtom)
    return quoteState.value?.quote
  })

  const wsUnsubscribeAtom = atom<{ unsubscribe: Unsubscribe } | undefined>(
    undefined,
  )
  const hasSubscriptionAtom = atom((ctx) => {
    return ctx.spy(wsUnsubscribeAtom) !== undefined
  })
  let reactionAtom: ReactionAtom<Promise<void>> | undefined = undefined

  const subscribePathfinderQuoteReaction = reaction(async (ctx) => {
    const {
      pathfinderWsClient,
      environment: { pathfinderEnabled },
    } = ctx.get(dependenciesAtom)
    if (!pathfinderEnabled) {
      return
    }

    const tradingWallet = ctx.spy(tradingWalletAtom)
    if (!tradingWallet) {
      return
    }

    const nativeCurrency = ctx.get(nativeNetworkCurrencyAtom)
    const inviteCode = ctx.get(joinedInviteCodeAtom)
    const tradeSettings = ctx.get(tradeSettingsAtom)
    if (tradeSettings.network !== Network.Solana) {
      return
    }

    const currencyIn = type === 'buy' ? pair.base : pair.quote
    const currencyOut = type === 'buy' ? pair.quote : pair.base
    const amountRaw =
      CurrencyModel.convertAmountToBigInt(currencyIn)(amount).toNumber()
    const slippageBps = tradeSettings.slippage * 100

    const tokenIn = PathfinderUtil.getPathfinderAddress(currencyIn)
    const tokenOut = PathfinderUtil.getPathfinderAddress(currencyOut)
    const rpc = ctx.get(solSelectedRpcAtom)
    const tip = solToLamports(tradeSettings.jitoTip)
    const priority = solToLamports(tradeSettings.priorityFee)

    quoteStateAtom.setLoading(ctx)
    subscribedAtAtom(ctx, new Date())

    const result = await ctx.schedule(() =>
      pathfinderWsClient.subscribeQuote(
        {
          token_in: tokenIn,
          token_out: tokenOut,
          amount: amountRaw,
          allow_split: true,
          referral_code: inviteCode,
          priority,
          tip,
          taker: tradingWallet.address,
          slippage: slippageBps,
          compute_unit: Quote.COMPUTE_UNIT,
          sender_type: TradeSettingsSolRpc.toPathfinderSenderType(rpc),
        },
        {
          onError: (message) => {
            if (message === 'Quote not found') {
              quoteStateAtom.setError(ctx, new QuoteNotFoundError())
            }
            quoteStateAtom.setError(ctx, UnknownError.fromUnknown(message))
          },
          onUpdate: (quoteDto) => {
            const quote = Quote.fromPathfinderDto(
              quoteDto,
              nativeCurrency,
              pair.quote,
            )
            if (quote) {
              const timestamp = new Date()
              if (ctx.get(firstQuoteReceivedAtAtom) === undefined) {
                firstQuoteReceivedAtAtom(ctx, timestamp)
              }
              quoteStateAtom.setSuccess(ctx, { quote, timestamp })
            } else {
              quoteStateAtom.setError(
                ctx,
                UnknownError.fromUnknown('No quote found'),
              )
            }
          },
        },
      ),
    )

    if (Result.isSuccess(result)) {
      wsUnsubscribeAtom(ctx, { unsubscribe: result.success.unsubscribe })
    }
  })

  const unsubscribePathfinderQuoteAction = action((ctx) => {
    reactionAtom?.unsubscribe()
    const subscription = ctx.get(wsUnsubscribeAtom)
    if (subscription) {
      subscription.unsubscribe()
    }
  })

  const subscribeAction = action((ctx) => {
    if (ctx.get(hasSubscriptionAtom)) {
      return
    }
    reactionAtom = spawn(ctx, (ctx) => subscribePathfinderQuoteReaction(ctx))
  })

  return quoteStateAtom.pipe(
    withAssign(() => ({
      quote: quoteAtom,
      subscribe: subscribeAction,
      subscribedAt: subscribedAtAtom,
      firstQuoteReceivedAt: firstQuoteReceivedAtAtom,
      hasSubscription: hasSubscriptionAtom,
      unsubscribe: unsubscribePathfinderQuoteAction,
    })),
  )
}

export type PathfinderQuoteSubscriptionModel = ReturnType<
  typeof reatomPathfinderQuoteSubscription
>
